/**
 * MobileBottomNav — PM bottom rail (W124.3 · 2026-05-26).
 *
 * Visible only on `<md`. Fixed bottom, 64px + safe-area-inset-bottom.
 *
 * Tabs (5, em ordem PM_NAV):
 *   Hoje · Plano · Compras · Obra · Mais
 *
 * "Mais" abre overlay full-screen com o resto do PM_NAV (Dinheiro,
 * Entrega, Configurações) + Admin section (quando admin). Owner /
 * client / broker não chegam aqui — `PortalShell` é o layout deles.
 *
 * Tab routing (via `getNavForRole(role, projectId)`):
 *   Hoje    → `/projects/:id/hoje` ou `/hoje` cross-obra hub
 *   Plano   → `/projects/:id/plano`        (disabled fora de obra)
 *   Compras → `/projects/:id/compras`      (disabled fora de obra)
 *   Obra    → `/projects/:id/obra`         (disabled fora de obra)
 *   Mais    → overlay (sem mudança de rota)
 *
 * Active-state: pathname exato OU `startsWith(${to}/)`. Para a Hoje
 * cross-obra (`/hoje`), também aceita a legacy `/inbox?tab=hoje` (mesma
 * compat do BottomTabs legado).
 *
 * Mobile-first: 375px sem scroll horizontal (grid dinâmico, primary+1 colunas iguais),
 * touch target ≥44pt (link h-full sobre tab h-16 → ~64px),
 * `pb-[env(safe-area-inset-bottom)]` preservado.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { getNavForRole, type ResolvedNavGroup } from '@/lib/nav-registry';
import type { UserRole } from '@builder/shared';

// ─────────────────────── helpers ──────────────────────────────────────

function extractProjId(pathname: string): string {
  const m = pathname.match(/^\/projects\/([^/]+)/);
  return m ? (m[1] ?? '') : '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveKey(key: string, t: any): string {
  const parts = key.split('.');
  let cursor: unknown = t;
  for (const part of parts) {
    if (cursor && typeof cursor === 'object' && part in (cursor as Record<string, unknown>)) {
      cursor = (cursor as Record<string, unknown>)[part];
    } else return key;
  }
  return typeof cursor === 'string' ? cursor : key;
}

function isActivePath(to: string, pathname: string, search: string): boolean {
  if (to === '/hoje') {
    if (pathname === '/hoje') return true;
    if (pathname === '/inbox' && new URLSearchParams(search).get('tab') === 'hoje') return true;
    return false;
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

// ─────────────────────── SVG icons ────────────────────────────────────

const ICON_PATHS: Record<string, string | string[]> = {
  Home: 'M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-4.5v-6.5h-7V21H4a1 1 0 0 1-1-1v-8.5z',
  FileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6 M8 13h8 M8 17h6'],
  ShoppingCart: ['M9 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z', 'M20 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z', 'M1 1h4l2.7 13.5a2 2 0 0 0 2 1.5h9.4a2 2 0 0 0 2-1.6L23 6H6'],
  HardHat: 'M4 13a8 8 0 0 1 16 0v3H4z M2 16h20 M9 13V8a3 3 0 0 1 6 0v5',
  Wallet: ['M21 12V7H5a2 2 0 0 1 0-4h14v4', 'M3 5v14a2 2 0 0 0 2 2h16v-5', 'M18 12a2 2 0 1 0 0 4h4v-4z'],
  Package: ['M16.5 9.4L7.5 4.21', 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z', 'M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12'],
  Settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.8 1.2V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-2.8-1.2l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9.9 6H12',
  Grid: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M3 14h7v7H3z', 'M14 14h7v7h-7z'],
};

function TabIcon({ icon }: { icon: string }) {
  const raw = ICON_PATHS[icon] ?? 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0';
  const paths = Array.isArray(raw) ? raw : [raw];
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

// ─────────────────────── MobileBottomNav ──────────────────────────────

/** IDs (em ordem) dos 4 grupos PM_NAV que vão pra bottom strip. */
const PRIMARY_IDS = ['hoje', 'plano', 'compras', 'obra'] as const;
type PrimaryId = (typeof PRIMARY_IDS)[number];

export function MobileBottomNav() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const projId = extractProjId(location.pathname);
  const role: UserRole | null = user?.role ?? null;

  // Close overlay on route change
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      setMoreOpen(false);
    }
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMoreOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const resolved = useMemo<ResolvedNavGroup[]>(
    () => (role ? getNavForRole(role, projId || undefined) : []),
    [role, projId],
  );

  if (!user || !role) return null;
  // Owner-portal roles use PortalShell — never render this bar.
  // `owner` is not in UserRole (portal uses client/broker), so guard
  // only those two values to satisfy the type-checker.
  if (role === 'client' || role === 'broker') return null;

  const groupsById: Record<string, ResolvedNavGroup | undefined> = {};
  for (const g of resolved) groupsById[g.id] = g;

  const primary = PRIMARY_IDS.map((id) => groupsById[id]).filter(
    (g): g is ResolvedNavGroup => !!g,
  );

  // Tudo que não é primário vai pra "Mais" (Dinheiro, Entrega,
  // Configurações, e Admin section quando admin).
  const overflow = resolved.filter(
    (g) => !(PRIMARY_IDS as readonly string[]).includes(g.id),
  );

  return (
    <>
      <nav
        aria-label={isEn ? 'Bottom navigation' : 'Navegação inferior'}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[35] md:hidden',
          'border-t border-line bg-surface/90 backdrop-blur-md',
          'supports-[backdrop-filter]:bg-surface/75',
          'pb-[env(safe-area-inset-bottom)]',
        )}
        style={{ height: '64px' }}
        data-testid="mobile-bottom-nav"
      >
        <ul
          className="mx-auto grid max-w-3xl h-full"
          // P2 (audit dummy): grid must size to the actual tab count. Crew
          // (CREW_NAV) only has hoje+obra in primary → 2 tabs + "Mais" = 3,
          // and a fixed repeat(5) left two dead columns. +1 for the "Mais" tab.
          style={{ gridTemplateColumns: `repeat(${primary.length + 1}, minmax(0, 1fr))` }}
        >
          {primary.map((g) => {
            const label = resolveKey(g.labelKey, t);
            const disabled = g.requiresProject && g.to.includes('/projects//');
            const active = !disabled && isActivePath(g.to, location.pathname, location.search);
            const content = (
              <>
                <TabIcon icon={g.icon} />
                <span className="truncate">{label}</span>
              </>
            );
            return (
              <li key={g.id}>
                {disabled ? (
                  // W153 P2 · linka pro picker de obra (`/campo`) em vez de
                  // virar <span> inerte sem caminho.
                  <Link
                    to="/campo"
                    aria-label={isEn ? `${label} — select a project first` : `${label} — selecione uma obra primeiro`}
                    title={isEn ? 'Select a project first' : 'Selecione uma obra primeiro'}
                    data-testid={`bottom-nav-${g.id}`}
                    className={cn(
                      'pressable relative flex h-full flex-col items-center justify-center gap-0.5 px-1 py-1',
                      'text-[11px] font-medium text-ink-soft opacity-50',
                    )}
                  >
                    {content}
                  </Link>
                ) : (
                  <Link
                    to={g.to}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`bottom-nav-${g.id}`}
                    className={cn(
                      'pressable relative flex h-full flex-col items-center justify-center gap-0.5 px-1 py-1',
                      'text-[11px] font-semibold',
                      active ? 'text-brand-400' : 'text-ink-muted font-medium hover:text-ink',
                    )}
                  >
                    {active && (
                      <span
                        aria-hidden
                        className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-6 rounded-b-full bg-brand-400"
                      />
                    )}
                    {content}
                  </Link>
                )}
              </li>
            );
          })}

          {/* Mais — opens overflow drawer */}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label={isEn ? 'More' : 'Mais'}
              aria-expanded={moreOpen}
              data-testid="bottom-nav-mais"
              className={cn(
                'pressable relative flex h-full w-full flex-col items-center justify-center gap-0.5 px-1 py-1',
                'text-[11px] font-medium text-ink-muted hover:text-ink',
                moreOpen && 'text-brand-400',
              )}
            >
              <TabIcon icon="Grid" />
              <span className="truncate">{isEn ? 'More' : 'Mais'}</span>
            </button>
          </li>
        </ul>
      </nav>

      {moreOpen && (
        <MoreOverlay
          groups={overflow}
          pathname={location.pathname}
          search={location.search}
          onClose={() => setMoreOpen(false)}
          t={t}
          isEn={isEn}
        />
      )}
    </>
  );
}

// ─────────────────────── MoreOverlay ──────────────────────────────────

function MoreOverlay({
  groups,
  pathname,
  search,
  onClose,
  t,
  isEn,
}: {
  groups: ResolvedNavGroup[];
  pathname: string;
  search: string;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
  isEn: boolean;
}) {
  const pm = groups.filter((g) => !g.isAdmin);
  const admin = groups.filter((g) => g.isAdmin);

  return (
    <>
      <div
        className="fixed inset-0 z-[45] bg-black/50 backdrop-blur-sm md:hidden"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEn ? 'More navigation options' : 'Mais opções de navegação'}
        data-testid="bottom-nav-mais-overlay"
        className={cn(
          'fixed inset-x-0 bottom-0 z-[50] md:hidden',
          'rounded-t-2xl border-t border-line bg-surface',
          'max-h-[80vh] overflow-y-auto',
          'pb-[env(safe-area-inset-bottom)]',
        )}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="mx-auto h-1 w-10 rounded-full bg-line" />
          <button
            type="button"
            onClick={onClose}
            aria-label={isEn ? 'Close' : 'Fechar'}
            className="pressable ml-4 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md px-2 py-1 text-sm text-ink-muted hover:bg-surface-2 hover:text-ink"
          >
            ✕
          </button>
        </div>

        <div className="p-3 space-y-1">
          {pm.length > 0 && (
            <>
              <p className="px-3 pt-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-soft">
                {isEn ? 'Navigation' : 'Navegação'}
              </p>
              <ul className="pb-1">
                {pm.map((g) => {
                  const label = resolveKey(g.labelKey, t);
                  const disabled = g.requiresProject && g.to.includes('/projects//');
                  const active = !disabled && isActivePath(g.to, pathname, search);
                  if (disabled) {
                    return (
                      <li key={g.id}>
                        <span
                          aria-label={label}
                          title={isEn ? 'Select a project' : 'Selecione uma obra'}
                          className="flex min-h-[44px] items-center rounded-lg px-3 py-2 text-[13px] text-ink-soft opacity-60 cursor-not-allowed"
                        >
                          {label}
                        </span>
                      </li>
                    );
                  }
                  return (
                    <li key={g.id}>
                      <Link
                        to={g.to}
                        onClick={onClose}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex min-h-[44px] items-center rounded-lg px-3 py-2 text-[13px] transition-colors',
                          active
                            ? 'bg-brand-500/10 text-brand-500'
                            : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                        )}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {admin.length > 0 && (
            <>
              <p className="px-3 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-soft">
                Admin
              </p>
              <ul className="pb-1">
                {admin.map((g) => {
                  const label = resolveKey(g.labelKey, t);
                  const active = isActivePath(g.to, pathname, search);
                  return (
                    <li key={g.id}>
                      <Link
                        to={g.to}
                        onClick={onClose}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex min-h-[44px] items-center rounded-lg px-3 py-2 text-[13px] transition-colors',
                          active
                            ? 'bg-brand-500/10 text-brand-500'
                            : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                        )}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
}
